import Component from "@glimmer/component";
import EmberObject from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import LinksRedirect from "discourse/components/links-redirect";
import PluginOutlet from "discourse/components/plugin-outlet";
import lazyHash from "discourse/helpers/lazy-hash";
import { and, eq, not, or } from "discourse/truth-helpers";
import dBoundAvatarTemplate from "discourse/ui-kit/helpers/d-bound-avatar-template";
import dDiscourseTags from "discourse/ui-kit/helpers/d-discourse-tags";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

function tagClasses(tagChanges, state, className) {
  return (tagChanges || []).reduce((classMap, tagChange) => {
    if (tagChange[state]) {
      classMap[tagChange.name] = className;
    }
    return classMap;
  }, {});
}

export default class Revisions extends Component {
  @service languageNameLookup;

  get fakePreviousTagsTopic() {
    // discourseTags expects a topic structure
    return EmberObject.create({
      tags: (this.args.previousTagChanges || []).map((tag) => tag.name),
    });
  }

  get previousTagClassesMap() {
    return tagClasses(this.args.previousTagChanges, "deleted", "diff-del");
  }

  get fakeCurrentTagsTopic() {
    return EmberObject.create({
      tags: (this.args.currentTagChanges || []).map((tag) => tag.name),
    });
  }

  get currentTagClassesMap() {
    return tagClasses(this.args.currentTagChanges, "inserted", "diff-ins");
  }

  get previousLocale() {
    const locale = this.args.model?.locale_changes?.previous;
    const language = this.languageNameLookup.getLanguageName(locale);
    return language || i18n("post.revisions.locale.no_locale_set");
  }

  get currentLocale() {
    const locale = this.args.model?.locale_changes?.current;
    const language = this.languageNameLookup.getLanguageName(locale);
    return language || i18n("post.revisions.locale.locale_removed");
  }

  <template>
    <div
      class={{@hiddenClasses}}
      data-post-id={{@model.post_id}}
      id="revisions"
      ...attributes
    >
      {{#if @model.locale_changes}}
        <div class="row revision__locale">
          <div class="revision-content --previous">
            <div class={{if @model.locale_changes.previous "diff-del"}}>
              {{dIcon "globe"}}
              {{this.previousLocale}}
            </div>
          </div>

          {{#if (or @mobileView (eq @viewMode "inline"))}}
            &rarr;&nbsp;
          {{/if}}

          <div class="revision-content --current">
            <div class={{if @model.locale_changes.current "diff-ins"}}>
              {{dIcon "globe"}}
              {{this.currentLocale}}
            </div>
          </div>
        </div>
      {{/if}}
      {{#if @model.title_changes}}
        <div class="row">
          <h2 class="revision__title">{{trustHTML @titleDiff}}</h2>
        </div>
      {{/if}}
      {{#if @mobileView}}
        {{#if @userChanges}}
          <div class="row">
            {{dBoundAvatarTemplate
              @model.user_changes.previous.avatar_template
              "small"
            }}
            {{@model.user_changes.previous.username}}
            &rarr;
            {{dBoundAvatarTemplate
              @model.user_changes.current.avatar_template
              "small"
            }}
            {{@model.user_changes.current.username}}
          </div>
        {{/if}}
        {{#if @model.reply_to_post_number_changes}}
          <div class="row reply-to-changes">
            {{dIcon "share"}}
            {{#if @model.reply_to_post_number_changes.previous}}
              #{{@model.reply_to_post_number_changes.previous.post_number}}
              {{#if
                @model.reply_to_post_number_changes.previous.avatar_template
              }}
                {{dBoundAvatarTemplate
                  @model.reply_to_post_number_changes.previous.avatar_template
                  "small"
                }}
                {{@model.reply_to_post_number_changes.previous.username}}
              {{/if}}
            {{else}}
              <span class="diff-del">{{i18n
                  "post.revisions.reply_to.none"
                }}</span>
            {{/if}}
            &rarr;
            {{#if @model.reply_to_post_number_changes.current}}
              #{{@model.reply_to_post_number_changes.current.post_number}}
              {{#if
                @model.reply_to_post_number_changes.current.avatar_template
              }}
                {{dBoundAvatarTemplate
                  @model.reply_to_post_number_changes.current.avatar_template
                  "small"
                }}
                {{@model.reply_to_post_number_changes.current.username}}
              {{/if}}
            {{else}}
              <span class="diff-ins">{{i18n
                  "post.revisions.reply_to.none"
                }}</span>
            {{/if}}
          </div>
        {{/if}}
        {{#if @model.wiki_changes}}
          <div class="row">
            {{dIcon
              "far-pen-to-square"
              class=(if @model.wiki_changes.current "diff-ins" "diff-del")
            }}
          </div>
        {{/if}}
        {{#if @model.archetype_changes}}
          <div class="row">
            {{dIcon
              (if
                (eq @model.archetype_changes.current "private_message")
                "envelope"
                "comment"
              )
            }}
          </div>
        {{/if}}
        {{#if (and @model.category_id_changes (not @model.archetype_changes))}}
          <div class="row">
            {{#if @previousCategory}}
              {{trustHTML @previousCategory}}
            {{else}}
              {{dIcon "far-eye-slash" class="diff-del"}}
            {{/if}}
            &rarr;
            {{#if @currentCategory}}
              {{trustHTML @currentCategory}}
            {{else}}
              {{dIcon "far-eye-slash" class="diff-ins"}}
            {{/if}}
          </div>
        {{/if}}
      {{/if}}
      {{#if @model.tags_changes}}
        <div class="row -tag-revisions">
          <span class="tag-revision__wrapper --previous">
            {{dDiscourseTags
              this.fakePreviousTagsTopic
              tagClasses=this.previousTagClassesMap
            }}
          </span>

          {{#if (or @mobileView (eq @viewMode "inline"))}}
            &rarr;&nbsp;
          {{/if}}

          <span class="tag-revision__wrapper --current">
            {{dDiscourseTags
              this.fakeCurrentTagsTopic
              tagClasses=this.currentTagClassesMap
            }}
          </span>
        </div>
      {{/if}}
      {{#if @model.featured_link_changes}}
        <div class="row">
          {{@model.featured_link_changes.previous}}
          &rarr;
          {{@model.featured_link_changes.current}}
        </div>
      {{/if}}

      <span>
        <PluginOutlet
          @connectorTagName="div"
          @name="post-revisions"
          @outletArgs={{lazyHash model=@model}}
        />
      </span>

      {{#if @diffHidden}}
        <div class="row revision__hidden-notice">
          {{dIcon "far-eye-slash"}}
          <span>{{i18n "post.revisions.diff_hidden"}}</span>
        </div>
      {{else}}
        <LinksRedirect
          class="row body-diff"
          {{didInsert @calculateBodyDiff @bodyDiffHTML}}
          {{didUpdate @calculateBodyDiff @bodyDiffHTML}}
        >
          {{trustHTML @bodyDiff}}
        </LinksRedirect>
      {{/if}}
    </div>
  </template>
}
